import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, TrendingUp, Phone, Mail, MapPin, Clock, 
  Filter, RefreshCw, CheckCircle, AlertCircle, 
  Flame, ArrowUp, ArrowDown, ChevronDown, Download,
  Search, LogOut, RotateCcw, Send, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceLabels = {
  'residential-painting': 'Painting',
  'remodeling-renovations': 'Remodeling',
  'drywall-repair': 'Drywall',
  'carpentry': 'Carpentry',
  'tile-installation': 'Tile',
  'exterior-siding': 'Siding',
  'deck-construction': 'Decks',
  'mold-remediation': 'Mold',
  'parking-lot-striping': 'Striping',
  'brick-masonry': 'Masonry',
  'roof-repairs': 'Roofing'
};

const PriorityBadge = ({ priority }) => {
  const configs = {
    hot: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-gray-400'
  };
  const labels = { hot: 'HOT', high: 'HIGH', medium: 'MED', low: 'LOW' };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${configs[priority] || configs.low}`}>
      {priority === 'hot' && <Flame className="w-3 h-3 inline mr-1" />}
      {labels[priority] || 'LOW'}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const configs = {
    new: 'bg-blue-500',
    contacted: 'bg-purple-500',
    quoted: 'bg-amber-500',
    converted: 'bg-green-500'
  };
  const labels = { new: 'New', contacted: 'Contacted', quoted: 'Quoted', converted: 'Converted' };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${configs[status] || configs.new}`}>
      {labels[status] || 'New'}
    </span>
  );
};

const DeliveryBadge = ({ status }) => {
  const configs = {
    pending: { bg: 'bg-gray-100 text-gray-600', icon: Clock },
    sent: { bg: 'bg-green-100 text-green-600', icon: Send },
    failed: { bg: 'bg-red-100 text-red-600', icon: AlertCircle },
  };
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${config.bg}`}>
      <Icon className="w-3 h-3" />
      {status || 'pending'}
    </span>
  );
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const LeadRow = ({ lead, expanded, onToggle, onStatusUpdate, onRetryEmail }) => {
  const score = lead.score || {};
  const bgClass = score.priority === 'hot' ? 'bg-red-50/50' : '';
  const avatarClass = score.priority === 'hot' ? 'bg-red-500' : 
                      score.priority === 'high' ? 'bg-orange-500' : 
                      score.priority === 'medium' ? 'bg-yellow-500' : 'bg-neutral-400';

  return (
    <React.Fragment>
      <tr className={`hover:bg-neutral-50 cursor-pointer transition-colors ${bgClass}`} onClick={onToggle} data-testid={`lead-row-${lead.id}`}>
        <td className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${avatarClass}`}>
              {lead.firstName?.[0]}{lead.lastName?.[0]}
            </div>
            <div>
              <p className="font-semibold text-neutral-900">{lead.firstName} {lead.lastName}</p>
              <p className="text-sm text-neutral-500">{lead.email}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          <span className="text-sm font-medium">{serviceLabels[lead.service] || lead.service}</span>
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="w-16 h-2 bg-neutral-200 rounded-full overflow-hidden">
              <div className={`h-full ${avatarClass}`} style={{ width: `${score.total || 0}%` }} />
            </div>
            <span className="text-sm font-bold">{score.total || 0}</span>
          </div>
        </td>
        <td className="px-4 py-4"><PriorityBadge priority={score.priority || 'low'} /></td>
        <td className="px-4 py-4"><StatusBadge status={lead.status} /></td>
        <td className="px-4 py-4"><DeliveryBadge status={lead.delivery_status} /></td>
        <td className="px-4 py-4 text-sm text-neutral-500">{formatDate(lead.created_at)}</td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <a href={`tel:${lead.phone}`} className="p-2 hover:bg-neutral-100 rounded-lg" onClick={e => e.stopPropagation()}><Phone className="w-4 h-4" /></a>
            <a href={`mailto:${lead.email}`} className="p-2 hover:bg-neutral-100 rounded-lg" onClick={e => e.stopPropagation()}><Mail className="w-4 h-4" /></a>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </td>
      </tr>
      
      {expanded && (
        <tr className="bg-neutral-50">
          <td colSpan={8} className="px-4 py-4">
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Contact Info</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-neutral-400" /><a href={`tel:${lead.phone}`} className="text-blue-600">{lead.phone}</a></div>
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-neutral-400" /><a href={`mailto:${lead.email}`} className="text-blue-600">{lead.email}</a></div>
                  {lead.city && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-neutral-400" />{lead.city}, TX</div>}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Project Details</h4>
                <p className="text-sm bg-white p-3 rounded-lg border">{lead.message || 'No details'}</p>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Attribution</h4>
                <div className="text-sm space-y-1 bg-white p-3 rounded-lg border">
                  {lead.utm_source && <p><span className="text-neutral-500">Source:</span> {lead.utm_source}</p>}
                  {lead.utm_medium && <p><span className="text-neutral-500">Medium:</span> {lead.utm_medium}</p>}
                  {lead.utm_campaign && <p><span className="text-neutral-500">Campaign:</span> {lead.utm_campaign}</p>}
                  {lead.gclid && <p><span className="text-neutral-500">GCLID:</span> <span className="text-green-600">Google Ads</span></p>}
                  {lead.landing_page && <p><span className="text-neutral-500">Landing:</span> {lead.landing_page}</p>}
                  {lead.referrer && <p><span className="text-neutral-500">Referrer:</span> {lead.referrer.substring(0, 30)}...</p>}
                  {!lead.utm_source && !lead.gclid && !lead.landing_page && <p className="text-neutral-400">No attribution data</p>}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Actions</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-neutral-500 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {['new', 'contacted', 'quoted', 'converted'].map((s) => (
                        <Button key={s} size="sm" variant={lead.status === s ? 'default' : 'outline'}
                          onClick={(e) => { e.stopPropagation(); onStatusUpdate(lead.id, s); }}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {lead.delivery_status === 'failed' && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600 border-red-300"
                      onClick={(e) => { e.stopPropagation(); onRetryEmail(lead.id); }}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Retry Email
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const AdminLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchLeads = useCallback(async () => {
    try {
      let url = `${API_URL}/api/leads?sort_by=${sortBy}`;
      if (priorityFilter !== 'all') url += `&priority=${priorityFilter}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        logout();
        return;
      }
      
      setLeads(await response.json());
    } catch (e) { 
      toast.error('Failed to fetch leads'); 
    }
  }, [sortBy, priorityFilter, statusFilter, searchTerm, logout]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/stats`, { credentials: 'include' });
      
      if (response.status === 401) {
        logout();
        return;
      }
      
      setStats(await response.json());
    } catch (e) { 
      toast.error('Failed to fetch stats'); 
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await fetch(`${API_URL}/api/leads/${leadId}/status?status=${status}`, { 
        method: 'PATCH',
        credentials: 'include'
      });
      toast.success('Status updated');
      fetchLeads();
      fetchStats();
    } catch (e) { 
      toast.error('Update failed'); 
    }
  };

  const retryEmail = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/retry-email`, { 
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success('Email resent successfully');
        fetchLeads();
      } else {
        toast.error(data.message || 'Failed to resend email');
      }
    } catch (e) { 
      toast.error('Failed to retry email'); 
    }
  };

  const exportCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/export`, { credentials: 'include' });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats()]);
      setLoading(false);
    };
    load();
  }, [fetchLeads]);

  const refresh = async () => {
    setLoading(true);
    await Promise.all([fetchLeads(), fetchStats()]);
    setLoading(false);
    toast.success('Refreshed');
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-dashboard">
      <header className="bg-neutral-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Lead Management Dashboard</h1>
            <p className="text-neutral-400 text-sm">Gray Contracting DFW • {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={exportCSV} variant="outline" className="border-neutral-700 text-white hover:bg-neutral-800" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
            <Button onClick={refresh} variant="outline" className="border-neutral-700 text-white hover:bg-neutral-800" disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button onClick={handleLogout} variant="ghost" className="text-neutral-400 hover:text-white hover:bg-neutral-800" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                <div><p className="text-2xl font-bold">{stats.total_leads}</p><p className="text-xs text-neutral-500">Total</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-red-500">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><Flame className="w-5 h-5 text-red-600" /></div>
                <div><p className="text-2xl font-bold">{stats.by_priority.hot}</p><p className="text-xs text-neutral-500">HOT</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500">
              <div><p className="text-2xl font-bold">{stats.by_priority.high}</p><p className="text-xs text-neutral-500">High</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-yellow-500">
              <div><p className="text-2xl font-bold">{stats.by_priority.medium}</p><p className="text-xs text-neutral-500">Medium</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
              <div><p className="text-2xl font-bold">{stats.by_status.converted}</p><p className="text-xs text-neutral-500">Converted</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
                <div><p className="text-2xl font-bold">{stats.action_needed}</p><p className="text-xs text-neutral-500">Action</p></div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.by_delivery?.failed > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <Send className={`w-5 h-5 ${stats.by_delivery?.failed > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_delivery?.failed || 0}</p>
                  <p className="text-xs text-neutral-500">Failed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-40" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="hot">HOT</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40" data-testid="sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Newest First</SelectItem>
                <SelectItem value="score">Highest Score</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-neutral-500">{leads.length} leads</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Lead</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Service</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {leads.map((lead) => (
                <LeadRow 
                  key={lead.id} 
                  lead={lead} 
                  expanded={expandedLead === lead.id}
                  onToggle={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                  onStatusUpdate={updateStatus}
                  onRetryEmail={retryEmail}
                />
              ))}
            </tbody>
          </table>
          {leads.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">No leads found</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminLeadsPage;
